import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import { RequiredKeys } from 'utility-types';

import { RatingsInsight, RatingsTotal } from 'apis/merchant-gateway/insights';
import { MOBILE_BREAKPOINT } from 'common/hooks/useIsMobile';
import { EntityType } from 'common/types';
import { Typography as T } from 'common/typography';

import { InsightResponseWithPreviousInterval } from '../../types';
import { formatGrowthRate, getGrowthRateForFields, getGrowthTrend } from '../../utils';
import MetricBlock from '../OverallPerformance/MetricBlock';
import CustomLegends from '../charts/CustomLegends';
import { Legend } from '../charts/types';

type RatingsSummaryProps = {
  ratingsInsight: InsightResponseWithPreviousInterval<RatingsInsight>;
  legends: Legend[];
  entityType: EntityType;
};

const MetricDivider = styled.div`
  margin: 0 -12px;
  border-inline-start: 1px solid ${cbSemanticCssVars.borderPrimary};

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    border-inline-start: none;
    border-block-start: 1px solid ${cbSemanticCssVars.borderPrimary};
    margin: -12px 0;
  }
`;
const RatingsSummaryContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px 0;
  flex-wrap: wrap;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-direction: column;
    padding: 0;
  }
`;
const RatingsSummarySection = styled.div`
  display: flex;
  gap: 48px;
  padding: 16px 24px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    flex-direction: column;
    gap: 32px;
    padding: 24px;
  }
`;

const BreakDownSection = styled.div`
  padding: 16px 24px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-end;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    border-block-start: 1px solid ${cbSemanticCssVars.borderPrimary};
    padding: 24px;
    align-items: flex-start;
  }
`;
const BreakDownTitle = styled(T.Body3)`
  align-self: flex-end;
  color: ${cbSemanticCssVars.textSecondary};

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    align-self: flex-start;
  }
`;

const DEFAULT_RATINGS_TOTAL: RatingsTotal = {
  positiveRatings: 0,
  negativeRatings: 0,
  totalRatings: 0,
  averageRating: 0,
};

export const RatingsSummary = ({ ratingsInsight, legends, entityType }: RatingsSummaryProps) => {
  const ratingsTotalForSelected = ratingsInsight.selected.data?.data?.ratings?.total ?? DEFAULT_RATINGS_TOTAL;

  const ratingsTotalForPrevious = ratingsInsight.previous.data?.data?.ratings?.total ?? DEFAULT_RATINGS_TOTAL;

  const { formatMessage, formatNumber, locale } = useIntl();

  const ratingsGrowthRates = getGrowthRateForFields(
    ratingsTotalForSelected as Pick<RatingsTotal, RequiredKeys<RatingsTotal>>,
    ratingsTotalForPrevious,
    ['positiveRatings', 'negativeRatings', 'totalRatings', 'averageRating'],
  );

  return (
    <RatingsSummaryContainer>
      <RatingsSummarySection>
        <MetricBlock
          title={formatMessage({ id: 'insights.ratings-and-reviews.in-selected-period' })}
          metrics={`${formatNumber(ratingsTotalForSelected.averageRating, { maximumFractionDigits: 1 })}/10`}
          growth={formatGrowthRate(ratingsGrowthRates.averageRating, locale)}
          growthTrend={getGrowthTrend(ratingsGrowthRates.averageRating)}
        />
        {entityType === 'venue' && (
          <MetricBlock
            title={formatMessage({ id: 'insights.ratings-and-reviews.lifetime-rating' })}
            tooltip={
              ratingsTotalForSelected.lifetimeRating == null
                ? formatMessage({ id: 'insights.ratings-and-reviews.lifetime-rating-empty-tooltip' })
                : formatMessage({ id: 'insights.ratings-and-reviews.lifetime-rating-tooltip' })
            }
            metrics={
              ratingsTotalForSelected.lifetimeRating == null
                ? '-'
                : `${formatNumber(ratingsTotalForSelected.lifetimeRating, { maximumFractionDigits: 1 })}/10`
            }
          />
        )}
        <MetricDivider />
        <MetricBlock
          title={formatMessage({ id: 'insights.ratings-and-reviews.total-reviews' })}
          metrics={formatNumber(ratingsTotalForSelected.totalRatings)}
          growth={formatGrowthRate(ratingsGrowthRates.totalRatings, locale)}
          growthTrend={getGrowthTrend(ratingsGrowthRates.totalRatings)}
        />
        <MetricBlock
          title={formatMessage({ id: 'insights.ratings-and-reviews.rated-positive' })}
          tooltip={formatMessage({ id: 'insights.ratings-and-reviews.rated-positive-tooltip' })}
          metrics={formatNumber(ratingsTotalForSelected.positiveRatings)}
          growth={formatGrowthRate(ratingsGrowthRates.positiveRatings, locale)}
          growthTrend={getGrowthTrend(ratingsGrowthRates.positiveRatings)}
        />
        <MetricBlock
          title={formatMessage({ id: 'insights.ratings-and-reviews.rated-negative' })}
          tooltip={formatMessage({ id: 'insights.ratings-and-reviews.rated-negative-tooltip' })}
          metrics={formatNumber(ratingsTotalForSelected.negativeRatings)}
          growth={formatGrowthRate(ratingsGrowthRates.negativeRatings, locale)}
          growthTrend={getGrowthTrend(ratingsGrowthRates.negativeRatings, true)}
        />
      </RatingsSummarySection>
      <BreakDownSection>
        <BreakDownTitle>
          <FormattedMessage id="insights.breakdown-by-day" />
        </BreakDownTitle>
        <CustomLegends legends={legends} />
      </BreakDownSection>
    </RatingsSummaryContainer>
  );
};
